import React from "react";
import { Link } from "react-router-dom";
import { Wand2, Film, Mic, Sparkles, ArrowRight, Crown, Zap, Lock } from "lucide-react";

const Feature = ({ icon: Icon, title, sub, accent }) => (
  <div data-testid={`feature-${title.toLowerCase().replace(/\s/g,'-')}`} className="studio-card p-6 group">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${accent ? "bg-gold text-black" : "bg-white/5 text-gold border border-gold/30"}`}>
      <Icon className="w-5 h-5" />
    </div>
    <h3 className="font-heading text-xl mb-2 tracking-tight">{title}</h3>
    <p className="text-white/60 text-sm leading-relaxed">{sub}</p>
  </div>
);

export default function Landing() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1513346940221-6f673d962e97?crop=entropy&cs=srgb&fm=jpg&q=85)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/60 via-obsidian/85 to-obsidian" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gold/30 mb-8">
              <Sparkles className="w-3.5 h-3.5 text-gold" />
              <span className="label-mono">PUB · IA · INDÉPENDANCE</span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] font-light">
              La création <span className="gold-text font-semibold">publicitaire</span><br/>
              à <span className="italic">votre</span> portée.
            </h1>
            <p className="mt-8 text-lg text-white/70 max-w-xl leading-relaxed">
              Flyers premium, vidéos courtes et voix off cinéma — générés par les IA les plus puissantes du marché. Sans budget pub, sans agence. <span className="text-gold">Juste votre vision.</span>
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/login?mode=signup" data-testid="hero-cta-start" className="gold-btn">
                Démarrer gratuitement <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/pricing" data-testid="hero-cta-pricing" className="ghost-btn">Voir les tarifs</Link>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl">
              {[
                { k: "Flyer", v: "30 sec" },
                { k: "Vidéo", v: "4 min" },
                { k: "Voix off", v: "Instant" },
              ].map((s) => (
                <div key={s.k}>
                  <div className="text-3xl font-heading gold-text font-semibold">{s.v}</div>
                  <div className="text-xs text-white/50 mt-1 uppercase tracking-widest">{s.k} en moyenne</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STUDIOS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid md:grid-cols-3 gap-2 mb-12 items-end">
          <div className="md:col-span-2">
            <span className="label-mono">Trois studios. Une seule plateforme.</span>
            <h2 className="mt-3 text-4xl lg:text-5xl tracking-tighter">
              Imaginez, l&apos;IA <span className="gold-text">crée</span>.
            </h2>
          </div>
          <p className="text-white/60">Importez votre logo, écrivez votre message, choisissez votre style. Le moteur fait le reste — vous exportez.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <Feature icon={Wand2} title="Flyer IA"
            sub="Gemini Nano Banana 3.1 — visuels publicitaires en haute définition à partir d'un prompt + logo optionnel. 4 styles : Premium, Minimaliste, Bold, Luxe doré." accent />
          <Feature icon={Film} title="Vidéo Pub"
            sub="Sora 2 — séquences vidéo 4 / 8 / 12 secondes, formats portrait, paysage, carré. Assemblage automatique avec effets cinéma." />
          <Feature icon={Mic} title="Voix Off Cinéma"
            sub="ElevenLabs multilingue v2 — voix d'hommes, femmes, enfants. Clonage de votre voix avec téléprompter live et effet cinéma." />
        </div>
      </section>

      {/* WORKFLOW */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5 md:sticky md:top-24">
            <span className="label-mono">Processus</span>
            <h2 className="mt-3 text-4xl tracking-tighter">De l&apos;idée à la pub diffusable, en minutes.</h2>
            <p className="mt-5 text-white/60">Pas besoin de monteur, ni de graphiste. XookPub orchestre les meilleurs modèles IA pour produire des assets pro, prêts à publier.</p>
          </div>
          <div className="md:col-span-7 space-y-4">
            {[
              {n:"01",t:"Décrivez votre publicité",d:"Public cible, message clé, ton — quelques mots suffisent."},
              {n:"02",t:"Ajoutez votre logo (optionnel)",d:"Glissez-déposez. Le moteur l'intègre dans la composition."},
              {n:"03",t:"L'IA construit le visuel ou la vidéo",d:"Nano Banana ou Sora 2 livrent en quelques secondes."},
              {n:"04",t:"Enregistrez ou générez la voix off",d:"Téléprompter live, micro intégré, effet cinéma."},
              {n:"05",t:"Exportez en HD",d:"Image PNG, vidéo MP4, audio MP3 — prêts pour réseaux sociaux ou impression."},
            ].map((s, i) => (
              <div key={s.n} className="gold-border-gradient p-6 flex gap-5 items-start" data-testid={`step-${i}`}>
                <div className="font-heading text-3xl gold-text">{s.n}</div>
                <div>
                  <div className="font-heading text-xl">{s.t}</div>
                  <div className="text-white/60 mt-1">{s.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROMO BANNER */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
        <div className="gold-border-gradient p-10 text-center">
          <Crown className="w-8 h-8 text-gold mx-auto" />
          <h3 className="mt-4 text-3xl font-heading">Accès gratuit pour les <span className="gold-text">5 premiers visionnaires</span></h3>
          <p className="mt-4 text-white/65 max-w-xl mx-auto">
            Code <span className="text-gold font-mono">XOOKPUB-OR-2026</span> — Mot de passe <span className="text-gold font-mono">Or2026!</span> (limité à 5 utilisateurs).
          </p>
          <Link to="/login?mode=signup" data-testid="promo-cta" className="gold-btn mt-6 inline-flex">Réclamer mon accès</Link>
        </div>
      </section>

      {/* WHY */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-3 gap-6">
        <Feature icon={Zap} title="Vitesse extrême" sub="30s pour un flyer, 4 min pour une vidéo. Tarifs imbattables." />
        <Feature icon={Lock} title="Vos créations vous appartiennent" sub="Tous droits commerciaux inclus. Exportez sans filigrane." />
        <Feature icon={Crown} title="Qualité publicitaire" sub="Les mêmes IA que les agences premium — démocratisées." />
      </section>
    </div>
  );
}
